import React, { useEffect } from 'react';
import { t } from '@lingui/macro';
import { Dialog, DialogProps } from '@economic/taco';
import { useLocation, useNavigate } from 'react-router-dom';

import SignupDialogContent from './components/SignupDialogContent';
import { useSignupDialogForm } from './hooks/useSignupDialogForm';
import { useSignupDialogNavigation } from './hooks/useSignupDialogNavigation';
import { useSignupDialogOnboardingContext } from './contexts/SignupDialogOnboardingContextProvider';
import { SmartPayCustomerType, SmartPayOnboardingSteps } from '../../../types';
import { postOnboardingInfo, postSubscribeToNewsletter } from './api';
import {
    trackSmartPayOnboarding,
    getPlanNameForSnowplowEvent,
    trackSmartPayOnboardingMixpanel,
    ONBOARDING_ERROR,
    SUCCESSFUL_ONBOARDING,
    SUCCESSFUL_ONBOARDING_MIXPANEL,
} from '../../tracking/tracking';

interface Props extends Partial<DialogProps> {
    refetchState?: () => void;
    inMarket?: boolean;
}

const SignupModal = ({ refetchState, inMarket, ...dialogProps }: Props) => {
    const { NFCustomerData, setOnboardError } = useSignupDialogOnboardingContext();

    const location = useLocation();
    const navigate = useNavigate();
    const { currentStep, resetFlow, setIsLoading } = useSignupDialogNavigation();
    const { formData, resetForm } = useSignupDialogForm();

    const customerType = NFCustomerData?.customerType ?? SmartPayCustomerType.NewCustomer;
    const customerTypeString = SmartPayCustomerType[customerType];

    useEffect(() => {
        if (currentStep !== SmartPayOnboardingSteps.SUCCESS || !formData) return;

        handleOnboarding();

        if (formData.newsletter)
            postSubscribeToNewsletter(
                formData.email || '',
                t`Yes, please, sign up for e-conomics' newsletter, which includes contains help for setting up and using Smart Pay, tips and tricks, invitations to events, as well as marketing about our accounting system and related software from our app partners.`
            );
    }, [currentStep]);

    const handleOnboarding = async () => {
        if (!NFCustomerData || !formData) return;
        // start loading
        setIsLoading(true);
        try {
            await postOnboardingInfo(formData, NFCustomerData?.customerType);

            trackSmartPayOnboardingMixpanel(SUCCESSFUL_ONBOARDING_MIXPANEL);
            trackSmartPayOnboarding(SUCCESSFUL_ONBOARDING, {
                Plan: getPlanNameForSnowplowEvent(formData?.subscriptionType),
                CustomerType: customerTypeString,
            });
        } catch (e) {
            trackSmartPayOnboarding(ONBOARDING_ERROR, { Error: e.toString(), CustomerType: customerTypeString });
            setOnboardError(e);
        } finally {
            // stop loading
            setIsLoading(false);
        }
    };

    const onDialogClose = (hide?: () => void) => {
        if (currentStep === SmartPayOnboardingSteps.SUCCESS) {
            refetchState?.();
        }

        hide?.();
        resetFlow();
        resetForm();

        if (inMarket) {
            // remove modal hash
            navigate(location.pathname);

            if (currentStep !== SmartPayOnboardingSteps.SUCCESS) return;

            // refresh page and close the modal after completing the onboarding
            if (window.location.pathname.includes('addon/smartpay')) {
                window.location.assign(location.pathname);
            } else {
                navigate('/market/addon/smartpay');
            }
        }
    };

    return (
        <Dialog size="sm" onClose={() => onDialogClose()} {...dialogProps}>
            <Dialog.Content aria-label={t`Smart Pay onboarding flow`}>
                {({ close }) => <SignupDialogContent closeDialog={() => onDialogClose(close)} />}
            </Dialog.Content>
        </Dialog>
    );
};

export default SignupModal;
